<template>
  <div class="cat">
    <div class="cat__head">
      <h4 class="mb-0">Справочник</h4>
      <div class="btn-group btn-group-sm cat__tabs" role="tablist" aria-label="Разделы справочника">
        <button
          v-for="t in tabOptions"
          :key="t.key"
          type="button"
          class="btn cat__tab"
          :class="tab === t.key ? 'btn-success' : 'btn-outline-secondary'"
          role="tab"
          :aria-selected="String(tab === t.key)"
          @click="setTab(t.key)"
        >{{ t.label }}</button>
      </div>
      <label class="cat__archived mb-0">
        <input v-model="showArchived" type="checkbox" class="cat__archived-input"> Показывать архив
      </label>
      <button type="button" class="btn btn-success btn-sm cat__create" :disabled="!canMutate" @click="openCreate">{{ createLabel }}</button>
    </div>

    <div v-for="f in failedResources" :key="f.key" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center cat__banner" role="alert">
      <span>Не удалось загрузить {{ f.label }}: {{ f.message }}</span>
      <button type="button" class="btn btn-sm btn-light ml-auto cat__retry" @click="f.retry()">Повторить</button>
    </div>

    <div v-if="!booted" class="cat__loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
    </div>

    <div v-else class="cat__body">
      <WorkTypesTable
        v-if="tab === 'works'"
        :rows="workTypes.data"
        :pending-ids="pendingWorkTypeIds"
        :disabled="!canMutate"
        @edit="openEditWorkType"
        @archive="archiveWorkType"
        @unarchive="unarchiveWorkType"
      />
      <ConsumablesTable
        v-else
        :rows="consumables.data"
        :pending-ids="pendingConsumableIds"
        :disabled="!canMutate"
        @edit="openEditConsumable"
        @archive="archiveConsumable"
        @unarchive="unarchiveConsumable"
      />
    </div>

    <WorkTypeFormModal
      v-if="workTypeModal"
      :key="'wt-' + modalKey(workTypeModal)"
      :row="workTypeModal.row"
      :consumables="activeConsumables"
      @saved="onWorkTypeSaved"
      @closed="workTypeModal = null"
    />
    <ConsumableFormModal
      v-if="consumableModal"
      :key="'cs-' + modalKey(consumableModal)"
      :row="consumableModal.row"
      @saved="onConsumableSaved"
      @closed="consumableModal = null"
    />
  </div>
</template>

<script>
import * as api from '@/components/management/dispatcher/api';
import {
  createResource, loadResource, isReady, errorMessage,
} from '@/components/management/dispatcher/resource';
import { isArchived } from '@/components/management/dispatcher/catalogPayload';
import { notifySuccess, notifyError } from '@/components/management/dispatcher/notify';
import WorkTypesTable from '@/components/management/dispatcher/catalog/WorkTypesTable.vue';
import ConsumablesTable from '@/components/management/dispatcher/catalog/ConsumablesTable.vue';
import WorkTypeFormModal from '@/components/management/dispatcher/catalog/WorkTypeFormModal.vue';
import ConsumableFormModal from '@/components/management/dispatcher/catalog/ConsumableFormModal.vue';

export const TABS = ['works', 'materials'];

/** Spec §6.2: unknown or missing value → works. */
export function readTab(query) {
  return query && query.tab === 'materials' ? 'materials' : 'works';
}

export default {
  name: 'CatalogSection',
  components: { WorkTypesTable, ConsumablesTable, WorkTypeFormModal, ConsumableFormModal },
  props: {
    layoutVersion: { type: Number, default: 0 },
  },
  data() {
    return {
      tab: 'works',
      showArchived: false,
      booted: false,
      consumables: createResource(),
      workTypes: createResource(),
      pendingWorkTypeIds: [],
      pendingConsumableIds: [],
      workTypeModal: null,
      consumableModal: null,
    };
  },
  computed: {
    tabOptions() {
      return [{ key: 'works', label: 'Виды работ' }, { key: 'materials', label: 'Номенклатура' }];
    },
    createLabel() { return this.tab === 'works' ? 'Новая работа' : 'Новый расходник'; },
    canMutate() {
      return this.tab === 'works'
        ? isReady(this.workTypes) && isReady(this.consumables)
        : isReady(this.consumables);
    },
    activeConsumables() { return this.consumables.data.filter((c) => !isArchived(c)); },
    failedResources() {
      const out = [];
      if (this.workTypes.status === 'error') out.push({ key: 'workTypes', label: 'виды работ', message: errorMessage(this.workTypes.error), retry: this.reloadWorkTypes });
      if (this.consumables.status === 'error') out.push({ key: 'consumables', label: 'расходники', message: errorMessage(this.consumables.error), retry: this.reloadConsumables });
      return out;
    },
  },
  watch: {
    showArchived() { this.reloadAll(); },
  },
  created() {
    this.inFlight = {}; // non-reactive: de-duplicates concurrent reloads per resource + mode
    this.queued = {}; // non-reactive: one coalesced trailing reload per key for `fresh` callers
    this.tab = readTab(this.$route && this.$route.query);
    this.loadInitial();
  },
  methods: {
    /* ---------- loading ---------- */
    fetchers() {
      return this.showArchived
        ? { consumables: api.getConsumablesIncludingArchived, workTypes: api.getWorkTypesIncludingArchived }
        : { consumables: api.getConsumables, workTypes: api.getWorkTypes };
    },
    /**
     * Reuses an in-flight load. With `fresh`, a load already in flight is followed by one more
     * run (coalesced across callers) so a post-mutation read never settles on a pre-mutation response.
     */
    dedupe(key, run, fresh = false) {
      const current = this.inFlight[key];
      if (current) {
        if (!fresh) return current;
        if (!this.queued[key]) {
          this.queued[key] = current.then(() => { this.queued[key] = null; return this.dedupe(key, run); });
        }
        return this.queued[key];
      }
      const promise = run().finally(() => { this.inFlight[key] = null; });
      this.inFlight[key] = promise;
      return promise;
    },
    reloadConsumables(fresh = false) {
      const mode = this.archiveMode();
      const fetch = this.fetchers().consumables;
      return this.dedupe(`consumables:${mode}`, () => loadResource(this.consumables, fetch), fresh)
        .then(() => (this.archiveMode() !== mode ? this.reloadConsumables() : undefined));
    },
    reloadWorkTypes(fresh = false) {
      const mode = this.archiveMode();
      const fetch = this.fetchers().workTypes;
      return this.dedupe(`workTypes:${mode}`, () => loadResource(this.workTypes, fetch), fresh)
        .then(() => (this.archiveMode() !== mode ? this.reloadWorkTypes() : undefined));
    },
    archiveMode() { return this.showArchived ? 'all' : 'active'; },
    reloadAll(fresh = false) {
      return Promise.all([this.reloadConsumables(fresh), this.reloadWorkTypes(fresh)]);
    },
    async loadInitial() {
      await this.reloadAll();
      this.booted = true;
    },
    /* ---------- tabs ---------- */
    setTab(tab) {
      if (!TABS.includes(tab) || tab === this.tab) return;
      this.tab = tab;
      if (this.$router && this.$route) {
        // vue-router 3.5 rejects replace() on a duplicated navigation; nothing to report.
        this.$router.replace({ query: { ...(this.$route.query || {}), tab } }).catch(() => {});
      }
    },
    modalKey(modal) { return modal && modal.row ? modal.row.id : 'new'; },
    /* ---------- row actions ---------- */
    async runRowAction(pendingKey, row, call, successText, reload) {
      const list = this[pendingKey];
      if (list.includes(row.id)) return;
      this[pendingKey] = [...list, row.id];
      try {
        await call(row.id);
        notifySuccess(this, successText);
        await reload();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this[pendingKey] = this[pendingKey].filter((id) => id !== row.id);
      }
    },
    archiveWorkType(row) {
      return this.runRowAction('pendingWorkTypeIds', row, api.archiveWorkType, 'Вид работ убран в архив', () => this.reloadWorkTypes(true));
    },
    unarchiveWorkType(row) {
      return this.runRowAction('pendingWorkTypeIds', row, api.unarchiveWorkType, 'Вид работ возвращён из архива', () => this.reloadWorkTypes(true));
    },
    archiveConsumable(row) {
      return this.runRowAction('pendingConsumableIds', row, api.archiveConsumable, 'Расходник убран в архив', () => this.reloadAll(true));
    },
    unarchiveConsumable(row) {
      return this.runRowAction('pendingConsumableIds', row, api.unarchiveConsumable, 'Расходник возвращён из архива', () => this.reloadAll(true));
    },
    /* ---------- modals ---------- */
    openCreate() {
      if (!this.canMutate) return;
      if (this.tab === 'works') this.workTypeModal = { row: null };
      else this.consumableModal = { row: null };
    },
    openEditWorkType(row) {
      if (!this.canMutate) return;
      this.workTypeModal = { row };
    },
    openEditConsumable(row) {
      if (!this.canMutate) return;
      this.consumableModal = { row };
    },
    onWorkTypeSaved() {
      this.workTypeModal = null;
      this.reloadWorkTypes(true);
    },
    onConsumableSaved() {
      this.consumableModal = null;
      this.reloadAll(true);
    },
  },
};
</script>

<style scoped lang="scss">
.cat { display: flex; flex-direction: column; height: 100%; min-height: 0; background: #fff; border: 1px solid #e3e5e1; border-radius: 8px; padding: 12px 16px; }
.cat__head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #eef0ec; margin-bottom: 8px; }
.cat__tabs .cat__tab { font-weight: 600; }
.cat__archived { margin-left: auto; font-size: 13px; color: #4c524d; display: flex; align-items: center; gap: 6px; cursor: pointer; }
.cat__loading { flex: 1; display: flex; align-items: center; justify-content: center; }
.cat__body { flex: 1; min-height: 0; overflow: auto; }
</style>
